"use client";

import { useAnimationSequence } from "@ui/hooks/useAnimationSequence";
import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { GridItem, MODEL_NAMES, TIMING, TRANSITIONS } from "../types";
import {
  PromptContentArea,
  ContentPlaceholder,
  ContentProcessingOverlay,
  PromptTextInput,
  TypingText,
  TextPlaceholder,
  PromptControlBar,
  ControlItem,
  IconImage,
  IconAspectRatio,
  IconQuality,
  IconLayers,
  SendButton,
} from "../components";

const SCENE_STATES = ["IDLE", "TYPING", "SENDING", "PROCESSING", "RESULT", "FINISHED", "RESET"] as const;

const IMAGE_GENERATE_RESULT = "/assets/images/image-video-hero/image-generate-result.webp";

export function ImageGenerateScene({
  onGenerationStarted,
  onComplete,
  autoStart = true,
}: {
  onGenerationStarted: (mediaUrl: string, mediaType: "image" | "video", label: string) => void;
  onComplete: (result: GridItem) => void;
  autoStart?: boolean;
}) {
  const t = useTranslations("ClientKeys.VideoImageGenerationHero");
  const { motion: m, start, store } = useAnimationSequence(SCENE_STATES);

  const promptText = t("prompt_image_generate");

  useEffect(() => {
    if (!autoStart) return;

    const timer = setTimeout(() => {
      start();
    }, 500);
    return () => clearTimeout(timer);
  }, [start, autoStart]);

  useEffect(() => {
    const unsubscribe = store.subscribe(
      state => state.state,
      currentState => {
        const currentStateName = SCENE_STATES[currentState];

        if (currentStateName === "PROCESSING") {
          onGenerationStarted(IMAGE_GENERATE_RESULT, "image", t("processing_generating_image"));
        }

        if (currentStateName === "FINISHED") {
          onComplete({
            type: "imageGenerate",
            media: "",
            status: "complete",
            imageUrl: IMAGE_GENERATE_RESULT,
            isImageResult: true,
          });
        }
      },
    );
    return unsubscribe;
  }, [store, onGenerationStarted, onComplete, t]);

  return (
    <>
      <PromptContentArea sequencedMotion={m}>
        <ContentPlaceholder
          sequencedMotion={m}
          initial={{ opacity: 1 }}
          animate={{
            FINISHED: { opacity: 1 },
          }}
          transition={{
            FINISHED: TRANSITIONS.instant,
          }}
        />

        <ContentProcessingOverlay
          sequencedMotion={m}
          render={["PROCESSING"]}
          initial={{ opacity: 0 }}
          animate={{
            PROCESSING: { opacity: 1 },
            RESULT: { opacity: 0 },
          }}
          transition={{
            PROCESSING: TRANSITIONS.medium,
            RESULT: TRANSITIONS.medium,
          }}
          spinner={
            <m.div
              animate={{
                PROCESSING: { rotate: 360 },
              }}
              transition={{
                PROCESSING: TRANSITIONS.spinner,
              }}
              className='tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full'
            />
          }
        />
      </PromptContentArea>

      <PromptTextInput sequencedMotion={m}>
        <TypingText
          sequencedMotion={m}
          text={promptText}
          render={["TYPING", "-FINISHED"]}
          animatePresence={{ initial: false }}
        >
          {promptText.split(" ").map((word: string, wordIndex: number) => (
            <m.span
              key={wordIndex}
              className='tw-inline'
              initial={{ opacity: 0 }}
              animate={{
                TYPING: { opacity: 1 },
              }}
              transition={{
                TYPING: { duration: 0.1, delay: TIMING.DELAY.SHORT + wordIndex * 0.1 },
              }}
            >
              {word}{" "}
            </m.span>
          ))}
        </TypingText>

        <TextPlaceholder
          sequencedMotion={m}
          text={t("prompt_image_placeholder")}
          render={["IDLE", "-TYPING", "FINISHED"]}
          animatePresence={{ initial: false }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...TRANSITIONS.medium, delay: 0.2 }}
        />
      </PromptTextInput>

      <PromptControlBar
        sequencedMotion={m}
        controls={
          <>
            <ControlItem
              icon={<IconImage className='tw-size-5' />}
              label={MODEL_NAMES.NANO_BANANA}
              sequencedMotion={m}
            />
            <ControlItem
              icon={<IconAspectRatio className='tw-size-5' />}
              label='16:9'
              sequencedMotion={m}
              className='tw-hidden md:tw-flex'
            />
            <ControlItem
              icon={<IconQuality className='tw-size-5' />}
              label='1K'
              sequencedMotion={m}
              className='tw-hidden md:tw-flex'
            />
            <ControlItem
              icon={<IconLayers className='tw-size-5' />}
              label='1'
              sequencedMotion={m}
              className='tw-hidden md:tw-flex'
            />
          </>
        }
        button={
          <SendButton
            sequencedMotion={m}
            animate={{
              SENDING: {
                scale: [1, 0.92, 1],
                backgroundColor: ["#F5F3F1", "#dbdad9", "#F5F3F1"],
              },
            }}
            transition={{
              SENDING: TRANSITIONS.slow,
            }}
          />
        }
      />
    </>
  );
}
